import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useHistory, useLocation } from 'react-router-dom';

import { CancelToken } from '../services/api';
import { GetUpdatedTokenAndRefresh } from '../services/auth';

import {
  createLocalStorageData,
  deleteLocalStorageData,
  getLocalStorageData,
} from '../utils/auth';

import { ProviderStateProps, UseAuthProps } from './types';

const AuthContext = createContext({} as UseAuthProps);

export const AuthProvider = ({ children }: { children?: ReactNode }) => {
  const location = useLocation();
  const history = useHistory();

  const [isLoggedOut, setIsLoggedOut] = useState(true);
  const [sessionData, setSessionData] = useState<ProviderStateProps | any>(
    getLocalStorageData()
  );

  const logout = useCallback(() => {
    setSessionData(null);
    deleteLocalStorageData();
    setIsLoggedOut(true);
    history.push('/login');
  }, [history]);

  useEffect(() => {
    const source = CancelToken.source();
    let timer: any = null;
    if (sessionData) createLocalStorageData(sessionData);
    if (location.pathname !== '/login') {
      if (!isLoggedOut) {
        timer = setTimeout(async () => {
          const { username, token, refresh } = sessionData || {};

          const response = await GetUpdatedTokenAndRefresh({
            username,
            token,
            refresh,
            cancelToken: source,
          });

          if(!response) {
            logout();
          }

          setSessionData({
            ...sessionData,
            username: sessionData?.username,
            token: response?.data.token,
            refresh: response?.data.refresh,
          });
        }, 60000);
      }

      return () => {
        clearTimeout(timer);
      };
    }
  }, [sessionData, location, isLoggedOut, logout]);

  return (
    <AuthContext.Provider
      value={{ sessionData, setSessionData, setIsLoggedOut, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext<UseAuthProps>(AuthContext);

export default AuthContext;
